import { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * ImageLightbox - Full screen image viewer with animated blur background
 * Features: smooth expansion animation, blur backdrop, keyboard navigation
 */
export const ImageLightbox = ({ 
  images = [], 
  initialIndex = 0, 
  isOpen, 
  onClose,
  alt = "Image"
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isAnimating, setIsAnimating] = useState(false);

  // Reset index and trigger animation when opening
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      // Trigger entrance animation
      requestAnimationFrame(() => {
        setIsAnimating(true);
      });
    } else {
      setIsAnimating(false);
    }
  }, [isOpen, initialIndex]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'Escape':
          handleClose();
          break;
        case 'ArrowLeft':
          goToPrev();
          break;
        case 'ArrowRight':
          goToNext();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, currentIndex]);

  const goToNext = useCallback(() => {
    if (images.length > 1) {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }
  }, [images.length]);

  const goToPrev = useCallback(() => {
    if (images.length > 1) {
      setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    }
  }, [images.length]);

  const handleClose = () => {
    setIsAnimating(false);
    // Wait for exit animation before closing
    setTimeout(() => {
      onClose();
    }, 200);
  };

  if (!isOpen || images.length === 0) return null;

  return (
    <div 
      className={`fixed inset-0 z-[100] flex items-center justify-center transition-all duration-300 ease-out ${
        isAnimating ? 'backdrop-blur-md bg-white/30' : 'backdrop-blur-none bg-transparent'
      }`}
      onClick={handleClose}
      data-testid="image-lightbox"
    >
      {/* Close button */}
      <button
        onClick={handleClose}
        className={`absolute top-4 right-4 p-2.5 rounded-full bg-[#1E3A5F] hover:bg-[#2a4a6f] text-white transition-all duration-300 z-10 shadow-lg ${
          isAnimating ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
        }`}
        data-testid="lightbox-close-btn"
        aria-label="Close lightbox"
      >
        <X className="w-5 h-5" />
      </button>

      {/* Image counter */}
      {images.length > 1 && (
        <div className={`absolute top-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-[#1E3A5F]/90 text-white text-sm font-medium shadow-lg transition-all duration-300 ${
          isAnimating ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
        }`}>
          {currentIndex + 1} / {images.length}
        </div>
      )}

      {/* Previous button */}
      {images.length > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            goToPrev();
          }}
          className={`absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-[#1E3A5F] hover:bg-[#2a4a6f] text-white transition-all duration-300 z-10 shadow-lg ${
            isAnimating ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
          }`}
          data-testid="lightbox-prev-btn"
          aria-label="Previous image"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
      )}

      {/* Main image container - fit to viewport height, natural aspect ratio */}
      <div
        className={`relative flex items-center justify-center transition-all duration-300 ease-out ${
          isAnimating
            ? 'opacity-100 scale-100'
            : 'opacity-0 scale-90'
        }`}
        style={{ height: 'calc(100vh - 140px)', width: '95vw' }}
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={images[currentIndex]}
          alt={`${alt} ${currentIndex + 1}`}
          className="object-contain rounded-lg shadow-2xl"
          style={{ maxHeight: '100%', maxWidth: '100%' }}
          draggable={false}
          data-testid="lightbox-image"
        />
      </div>

      {/* Next button */}
      {images.length > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            goToNext();
          }}
          className={`absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-[#1E3A5F] hover:bg-[#2a4a6f] text-white transition-all duration-300 z-10 shadow-lg ${
            isAnimating ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
          }`}
          data-testid="lightbox-next-btn"
          aria-label="Next image"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      )}

      {/* Thumbnail strip at bottom */}
      {images.length > 1 && (
        <div className={`absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 p-2 rounded-xl bg-white/80 backdrop-blur-sm shadow-lg transition-all duration-300 ${
          isAnimating ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          {images.map((img, idx) => (
            <button
              key={idx}
              onClick={(e) => {
                e.stopPropagation();
                setCurrentIndex(idx);
              }}
              className={`w-14 h-14 rounded-lg overflow-hidden transition-all duration-200 ${
                idx === currentIndex 
                  ? 'ring-2 ring-[#1E3A5F] ring-offset-2 scale-105' 
                  : 'opacity-70 hover:opacity-100'
              }`}
            >
              <img 
                src={img} 
                alt={`Thumbnail ${idx + 1}`} 
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
